import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";
import {
  Search,
  DollarSign,
  Send,
  Clock,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  Calendar,
  FileText,
  ArrowRight,
  RefreshCw,
  FolderKanban,
} from "lucide-react";
import { toast } from "sonner";

export default function PartnerRequestsPage() {
  const { user } = useAuth();
  const [priceRequests, setPriceRequests] = useState([]);
  const [clientRequests, setClientRequests] = useState([]);
  const [taskChangeRequests, setTaskChangeRequests] = useState([]);
  const [studioRequests, setStudioRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("studio"); // Default to studio requests (needs attention)
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Task change request dialog state
  const [selectedChangeRequest, setSelectedChangeRequest] = useState(null);
  const [changeRequestComment, setChangeRequestComment] = useState("");
  const [submittingChangeRequest, setSubmittingChangeRequest] = useState(false);
  
  // Studio request dialog state
  const [selectedStudioRequest, setSelectedStudioRequest] = useState(null);
  const [studioRequestComment, setStudioRequestComment] = useState("");
  const [submittingStudioRequest, setSubmittingStudioRequest] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [priceRes, clientRes, changeRes, studioRes] = await Promise.all([
        api.get("/price-requests").catch(() => ({ data: [] })),
        api.get("/client-requests").catch(() => ({ data: [] })),
        api.get("/partner/task-change-requests").catch(() => ({ data: [] })),
        api.get("/studio-requests").catch(() => ({ data: [] })),
      ]);
      setPriceRequests(priceRes.data || []);
      setClientRequests(clientRes.data || []);
      setTaskChangeRequests(changeRes.data || []);
      setStudioRequests(studioRes.data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle task change request approval/rejection
  const handleChangeRequestResponse = async (requestId, action) => {
    setSubmittingChangeRequest(true);
    try {
      await api.put(`/partner/task-change-requests/${requestId}`, {
        action: action,
        comment: changeRequestComment
      });
      toast.success(action === "approve" ? "Changes approved!" : "Changes rejected");
      setSelectedChangeRequest(null);
      setChangeRequestComment("");
      fetchData();
    } catch (error) {
      console.error("Error responding to change request:", error);
      toast.error("Failed to process request");
    } finally {
      setSubmittingChangeRequest(false);
    }
  };
  
  // Handle studio request response (approve/reject)
  const handleStudioRequestResponse = async (requestId, action) => {
    setSubmittingStudioRequest(true);
    try {
      await api.put(`/studio-requests/${requestId}/respond`, {
        action: action,
        comment: studioRequestComment
      });
      toast.success(action === "approve" ? "Request approved!" : "Request rejected");
      setSelectedStudioRequest(null);
      setStudioRequestComment("");
      fetchData();
    } catch (error) {
      console.error("Error responding to studio request:", error);
      toast.error("Failed to process request");
    } finally {
      setSubmittingStudioRequest(false);
    }
  };
  
  // Format change type for display
  const getChangeTypeLabel = (type) => {
    switch (type) {
      case "budget_change": return "Budget Change";
      case "deadline_change": return "Deadline Change";
      case "scope_change": return "Scope Change";
      default: return type;
    }
  };
  
  const getChangeTypeIcon = (type) => {
    switch (type) {
      case "budget_change": return <DollarSign className="h-4 w-4 text-green-400" />;
      case "deadline_change": return <Calendar className="h-4 w-4 text-blue-400" />;
      case "scope_change": return <FileText className="h-4 w-4 text-purple-400" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  // Price requests stats
  const priceStats = useMemo(() => ({
    total: priceRequests.length,
    pending: priceRequests.filter(r => r.status === "pending").length,
    approved: priceRequests.filter(r => r.status === "approved").length,
    totalAmount: priceRequests.reduce((sum, r) => sum + (r.proposed_price || 0) * (r.quantity || 1), 0),
  }), [priceRequests]);

  // Client requests stats
  const clientStats = useMemo(() => ({
    total: clientRequests.length,
    pending: clientRequests.filter(r => r.status === "pending").length,
    responded: clientRequests.filter(r => r.status === "responded").length,
  }), [clientRequests]);
  
  // Task change requests stats
  const changeStats = useMemo(() => ({
    total: taskChangeRequests.length,
    budget: taskChangeRequests.filter(r => r.change_type === "budget_change").length,
    deadline: taskChangeRequests.filter(r => r.change_type === "deadline_change").length,
    scope: taskChangeRequests.filter(r => r.change_type === "scope_change").length,
  }), [taskChangeRequests]);
  
  // Studio requests stats (from studio to partner)
  const studioStats = useMemo(() => ({
    total: studioRequests.length,
    pending: studioRequests.filter(r => r.status === "pending").length,
    approved: studioRequests.filter(r => r.status === "approved").length,
    rejected: studioRequests.filter(r => r.status === "rejected").length,
  }), [studioRequests]);
  
  // Filtered studio requests
  const filteredStudioRequests = useMemo(() => {
    let result = studioRequests;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.title?.toLowerCase().includes(q) || r.project_name?.toLowerCase().includes(q)
      );
    }
    if (filterStatus !== "all") result = result.filter(r => r.status === filterStatus);
    return result;
  }, [studioRequests, searchQuery, filterStatus]);

  // Filtered requests based on active tab
  const filteredPriceRequests = useMemo(() => {
    let result = priceRequests;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.task_name?.toLowerCase().includes(q) || r.project_name?.toLowerCase().includes(q)
      );
    }
    if (filterStatus !== "all") result = result.filter(r => r.status === filterStatus);
    return result;
  }, [priceRequests, searchQuery, filterStatus]);

  const filteredClientRequests = useMemo(() => {
    let result = clientRequests;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.title?.toLowerCase().includes(q) || r.project_name?.toLowerCase().includes(q)
      );
    }
    if (filterStatus !== "all") {
      if (filterStatus === "pending") result = result.filter(r => r.status === "pending");
      if (filterStatus === "responded") result = result.filter(r => r.status === "responded");
    }
    return result;
  }, [clientRequests, searchQuery, filterStatus]);

  const formatPrice = (p) => p ? `$${p.toLocaleString()}` : "—";
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { day: "2-digit", month: "short" }) : "";

  const getTypeLabel = (type) => {
    const labels = {
      exterior_render: "Exterior", interior_render: "Interior", aerial_view: "Aerial",
      animation: "Animation", floor_plan: "Floor Plan", vr: "VR", vexos: "VEXOS",
    };
    return labels[type] || type?.replace(/_/g, " ") || "—";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="partner-requests-page">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-zinc-50">Requests</h1>
        <p className="text-zinc-500 text-xs">
          {studioStats.pending > 0 && <span className="text-red-400 font-medium">{studioStats.pending} from studio • </span>}
          {clientStats.pending} info requests • {priceStats.pending} price requests • {changeStats.total} task changes
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setFilterStatus("all"); }}>
        <TabsList className="bg-zinc-800/50">
          <TabsTrigger value="studio" className="data-[state=active]:bg-zinc-700">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            From Studio
            {studioStats.pending > 0 && (
              <Badge className="ml-2 bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1.5 animate-pulse">
                {studioStats.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="client" className="data-[state=active]:bg-zinc-700">
            <Send className="h-3.5 w-3.5 mr-1.5" />
            Info Requests
            {clientStats.pending > 0 && (
              <Badge className="ml-2 bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] px-1.5">
                {clientStats.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="price" className="data-[state=active]:bg-zinc-700">
            <DollarSign className="h-3.5 w-3.5 mr-1.5" />
            Price Requests
            {priceStats.pending > 0 && (
              <Badge className="ml-2 bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px] px-1.5">
                {priceStats.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="changes" className="data-[state=active]:bg-zinc-700">
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Task Changes
            {changeStats.total > 0 && (
              <Badge className="ml-2 bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] px-1.5">
                {changeStats.total}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Studio Requests Tab (FROM Studio TO Partner - needs approval) */}
        <TabsContent value="studio" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="flex gap-2 text-xs">
            {[
              { key: "all", label: "All", count: studioStats.total, color: "text-zinc-400" },
              { key: "pending", label: "Pending", count: studioStats.pending, color: "text-red-400" },
              { key: "approved", label: "Approved", count: studioStats.approved, color: "text-green-400" },
              { key: "rejected", label: "Rejected", count: studioStats.rejected, color: "text-zinc-500" },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setFilterStatus(s.key)}
                className={cn(
                  "px-2 py-1 rounded transition-colors",
                  filterStatus === s.key ? "bg-zinc-700" : "hover:bg-zinc-800"
                )}
              >
                <span className={s.color}>{s.label}</span>
                <span className="ml-1 text-zinc-500">({s.count})</span>
              </button>
            ))}
          </div>

          {/* Studio Requests List */}
          {filteredStudioRequests.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-8 text-center text-zinc-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No requests from studio</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredStudioRequests.map(req => {
                const hasPrice = req.price_per_unit > 0 || req.amount > 0;
                const totalAmount = req.amount || (req.price_per_unit > 0 && req.quantity > 0 
                  ? req.price_per_unit * (req.quantity || 1) * (1 - (req.discount || 0) / 100) 
                  : 0);
                
                return (
                <Card 
                  key={req.id} 
                  className={cn(
                    "bg-zinc-900/50 border-zinc-800 transition-colors",
                    req.status === "pending" && "border-l-2 border-l-red-500"
                  )}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 cursor-pointer" onClick={() => setSelectedStudioRequest(req)}>
                        <div className="flex items-center gap-2">
                          <Badge className={cn(
                            "text-[10px]",
                            req.priority === "urgent" && "bg-red-500/20 text-red-400",
                            req.priority === "high" && "bg-orange-500/20 text-orange-400",
                            req.priority === "normal" && "bg-blue-500/20 text-blue-400",
                            req.priority === "low" && "bg-zinc-500/20 text-zinc-400",
                          )}>
                            {req.priority}
                          </Badge>
                          <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">
                            {req.request_type?.replace(/_/g, " ")}
                          </Badge>
                          <span className="text-sm font-medium text-zinc-50">{req.title}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <FolderKanban className="h-3 w-3" />
                            {req.project_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(req.created_at)}
                          </span>
                        </div>
                        {/* Price block - prominent */}
                        {hasPrice ? (
                          <div className="mt-2 px-3 py-1.5 bg-zinc-800/80 rounded-md inline-flex items-center gap-3">
                            {req.price_per_unit > 0 && (
                              <span className="text-sm text-zinc-300 font-mono">
                                ${req.price_per_unit.toLocaleString()} × {req.quantity || 1}
                              </span>
                            )}
                            {req.discount > 0 && (
                              <span className="text-sm text-orange-400 font-medium">-{req.discount}%</span>
                            )}
                            {totalAmount > 0 && (
                              <span className="text-base text-green-400 font-bold font-mono">
                                = ${totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </span>
                            )}
                          </div>
                        ) : (
                          req.description && (
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{req.description}</p>
                          )
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        {req.status === "pending" ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={(e) => { e.stopPropagation(); handleStudioRequestResponse(req.id, "reject"); }}
                              disabled={submittingStudioRequest}
                              data-testid={`reject-studio-req-${req.id}`}
                            >
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700"
                              onClick={(e) => { e.stopPropagation(); handleStudioRequestResponse(req.id, "approve"); }}
                              disabled={submittingStudioRequest}
                              data-testid={`approve-studio-req-${req.id}`}
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                          </>
                        ) : (
                          <Badge className={cn(
                            "text-[10px]",
                            req.status === "approved" && "bg-green-500/20 text-green-400",
                            req.status === "rejected" && "bg-red-500/20 text-red-400",
                          )}>
                            {req.status}
                          </Badge>
                        )}
                        <ChevronRight className="h-4 w-4 text-zinc-600 cursor-pointer" onClick={() => setSelectedStudioRequest(req)} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          )}
        </TabsContent>

        {/* Client Requests Tab */}
        <TabsContent value="client" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="flex gap-2 text-xs">
            {[
              { key: "all", label: "All", count: clientStats.total, color: "text-zinc-400" },
              { key: "pending", label: "Pending", count: clientStats.pending, color: "text-orange-400" },
              { key: "responded", label: "Responded", count: clientStats.responded, color: "text-green-400" },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setFilterStatus(s.key)}
                className={cn("px-3 py-1.5 rounded-md", filterStatus === s.key ? "bg-zinc-800" : "hover:bg-zinc-800/50")}
              >
                <span className={s.color}>{s.count}</span>
                <span className="text-zinc-500 ml-1">{s.label}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm bg-zinc-900 border-zinc-800"
            />
          </div>

          {/* Client Requests List */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              {filteredClientRequests.length === 0 ? (
                <div className="p-6 text-center text-zinc-500 text-sm">No requests</div>
              ) : (
                <div className="divide-y divide-zinc-800/50">
                  {filteredClientRequests.map(req => (
                    <Link
                      key={req.id}
                      to={`/client-requests/${req.id}`}
                      className={cn(
                        "flex items-center justify-between p-3 hover:bg-zinc-800/30",
                        req.status === "pending" && "bg-orange-500/5"
                      )}
                      data-testid={`client-request-${req.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center",
                          req.status === "pending" ? "bg-orange-500/10" : "bg-green-500/10"
                        )}>
                          {req.status === "pending" ? (
                            <Clock className="h-4 w-4 text-orange-400" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-zinc-50 text-sm font-medium">{req.title}</p>
                          <p className="text-zinc-500 text-xs">{req.project_name || "No project"} • {req.request_type?.replace(/_/g, " ")}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {req.is_overdue && req.status === "pending" && (
                          <AlertTriangle className="h-4 w-4 text-red-400" />
                        )}
                        <Badge variant="outline" className={cn("text-[10px]",
                          req.status === "responded" 
                            ? "bg-green-500/10 text-green-400 border-green-500/30"
                            : req.is_overdue
                              ? "bg-red-500/10 text-red-400 border-red-500/30"
                              : "bg-orange-500/10 text-orange-400 border-orange-500/30"
                        )}>
                          {req.status === "responded" ? "Responded" : req.is_overdue ? "OVERDUE" : "Pending"}
                        </Badge>
                        <ChevronRight className="h-4 w-4 text-zinc-600" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price Requests Tab */}
        <TabsContent value="price" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="flex gap-2 text-xs">
            {[
              { key: "all", label: "All", count: priceStats.total, color: "text-zinc-400" },
              { key: "pending", label: "Pending", count: priceStats.pending, color: "text-orange-400" },
              { key: "approved", label: "Approved", count: priceStats.approved, color: "text-green-400" },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setFilterStatus(s.key)}
                className={cn("px-3 py-1.5 rounded-md", filterStatus === s.key ? "bg-zinc-800" : "hover:bg-zinc-800/50")}
              >
                <span className={s.color}>{s.count}</span>
                <span className="text-zinc-500 ml-1">{s.label}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm bg-zinc-900 border-zinc-800"
            />
          </div>

          {/* Price Requests Table */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 border-b border-zinc-800 text-[10px] text-zinc-500 uppercase">
                <div className="col-span-4">Task</div>
                <div className="col-span-2">Project</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-1 text-center">Qty</div>
                <div className="col-span-1 text-right">Price</div>
                <div className="col-span-2">Status</div>
              </div>
              
              {filteredPriceRequests.length === 0 ? (
                <div className="p-6 text-center text-zinc-500 text-sm">No price requests</div>
              ) : (
                <div className="divide-y divide-zinc-800/50">
                  {filteredPriceRequests.map(req => (
                    <div
                      key={req.id}
                      className={cn("grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm",
                        req.status === "pending" && "bg-orange-500/5"
                      )}
                    >
                      <div className="col-span-4 flex items-center gap-2">
                        <DollarSign className={cn("h-3.5 w-3.5",
                          req.status === "pending" ? "text-orange-400" : "text-green-400"
                        )} />
                        <span className="text-zinc-50 truncate">{req.task_name}</span>
                      </div>
                      <div className="col-span-2 text-zinc-400 truncate text-xs hidden md:block">{req.project_name}</div>
                      <div className="col-span-2 text-zinc-500 text-xs hidden md:block">{getTypeLabel(req.task_type)}</div>
                      <div className="col-span-1 text-center text-zinc-400 text-xs hidden md:block">{req.quantity || 1}</div>
                      <div className="col-span-1 text-right text-zinc-50 font-mono text-xs hidden md:block">{formatPrice(req.proposed_price)}</div>
                      <div className="col-span-2 hidden md:flex items-center gap-2">
                        <Badge variant="outline" className={cn("text-[10px]",
                          req.status === "approved" ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-orange-500/10 text-orange-400 border-orange-500/30"
                        )}>
                          {req.status}
                        </Badge>
                      </div>
                      <div className="col-span-8 flex items-center justify-between md:hidden">
                        <Badge variant="outline" className={cn("text-[10px]",
                          req.status === "approved" ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-orange-500/10 text-orange-400 border-orange-500/30"
                        )}>
                          {req.status}
                        </Badge>
                        <span className="text-zinc-50 font-mono text-xs">{formatPrice(req.proposed_price)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {priceRequests.length > 0 && (
            <div className="text-right text-xs text-zinc-500">
              Total: <span className="text-zinc-50 font-mono ml-1">{formatPrice(priceStats.totalAmount)}</span>
            </div>
          )}
        </TabsContent>
        
        {/* Task Changes Tab */}
        <TabsContent value="changes" className="space-y-4 mt-4">
          {/* Stats */}
          <div className="flex gap-2 text-xs">
            {[
              { key: "all", label: "All", count: changeStats.total, color: "text-zinc-400" },
              { key: "budget", label: "Budget", count: changeStats.budget, color: "text-green-400" },
              { key: "deadline", label: "Deadline", count: changeStats.deadline, color: "text-blue-400" },
              { key: "scope", label: "Scope", count: changeStats.scope, color: "text-purple-400" },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setFilterStatus(s.key)}
                className={cn("px-3 py-1.5 rounded-md", filterStatus === s.key ? "bg-zinc-800" : "hover:bg-zinc-800/50")}
              >
                <span className={s.color}>{s.count}</span>
                <span className="text-zinc-500 ml-1">{s.label}</span>
              </button>
            ))}
          </div>
          
          {/* Task Change Requests List */}
          {taskChangeRequests.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6 text-center text-zinc-500 text-sm">
                No pending task change requests
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {taskChangeRequests
                .filter(req => filterStatus === "all" || req.change_type === `${filterStatus}_change`)
                .map(req => (
                <Card 
                  key={req.id} 
                  className="border border-amber-500/30 bg-amber-900/10 hover:bg-amber-900/20 transition-all"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-2 rounded-lg bg-amber-500/20">
                          {getChangeTypeIcon(req.change_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h4 className="font-semibold text-zinc-50">{req.task_name || "Task"}</h4>
                            <Badge variant="outline" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">
                              {getChangeTypeLabel(req.change_type)}
                            </Badge>
                          </div>
                          
                          {/* Project name */}
                          <p className="text-xs text-zinc-500 mb-2">
                            <FolderKanban className="h-3 w-3 inline mr-1" />
                            {req.project_name || "Project"}
                          </p>
                          
                          {/* Change details */}
                          <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50 space-y-2">
                            {(req.change_type === "budget_change" || req.change_type === "scope_change") && 
                              (req.old_client_price != null || req.original_client_price != null) && 
                              req.new_client_price != null && (
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-zinc-400">Budget:</span>
                                  <span className="text-red-400 line-through">
                                    ${(req.old_client_price ?? req.original_client_price)?.toLocaleString()}
                                  </span>
                                  <ArrowRight className="h-4 w-4 text-zinc-500" />
                                  <span className="text-green-400 font-semibold">
                                    ${req.new_client_price?.toLocaleString()}
                                  </span>
                                </div>
                              )
                            }
                            
                            {req.change_type === "deadline_change" && (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-zinc-400">Deadline:</span>
                                {(req.old_deadline || req.original_deadline) && (
                                  <>
                                    <span className="text-red-400 line-through">
                                      {formatDate(req.old_deadline || req.original_deadline)}
                                    </span>
                                    <ArrowRight className="h-4 w-4 text-zinc-500" />
                                  </>
                                )}
                                <span className="text-green-400 font-semibold">
                                  {req.new_deadline ? formatDate(req.new_deadline) : "TBD"}
                                </span>
                              </div>
                            )}
                            
                            {req.reason && (
                              <div className="text-sm">
                                <span className="text-zinc-400">Reason: </span>
                                <span className="text-zinc-300">{req.reason}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Request metadata */}
                          <p className="text-xs text-zinc-500 mt-2">
                            Requested by: {req.created_by_name || "Manager"} • 
                            {req.created_at && ` ${formatDate(req.created_at)}`}
                          </p>
                        </div>
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => setSelectedChangeRequest(req)}
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          Review
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Task Change Request Approval Dialog */}
      <Dialog 
        open={!!selectedChangeRequest} 
        onOpenChange={() => { setSelectedChangeRequest(null); setChangeRequestComment(""); }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Task Change Request
            </DialogTitle>
          </DialogHeader>
          
          {selectedChangeRequest && (
            <div className="space-y-4 py-2">
              {/* Task & Project info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-sm font-medium text-zinc-50">{selectedChangeRequest.task_name}</p>
                <p className="text-xs text-zinc-500">{selectedChangeRequest.project_name}</p>
              </div>
              
              {/* Change type badge */}
              <Badge variant="outline" className="text-sm bg-amber-500/20 text-amber-400 border-amber-500/30">
                {getChangeTypeLabel(selectedChangeRequest.change_type)}
              </Badge>
              
              {/* Change details */}
              <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700/50 space-y-3">
                {(selectedChangeRequest.change_type === "budget_change" || selectedChangeRequest.change_type === "scope_change") && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">Current budget:</span>
                      <span className="text-sm text-red-400 line-through">
                        ${(selectedChangeRequest.old_client_price || selectedChangeRequest.original_client_price)?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">New budget:</span>
                      <span className="text-sm text-green-400 font-semibold">
                        ${selectedChangeRequest.new_client_price?.toLocaleString()}
                      </span>
                    </div>
                    {selectedChangeRequest.old_client_price && selectedChangeRequest.new_client_price && (
                      <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
                        <span className="text-sm text-zinc-400">Difference:</span>
                        <span className={cn(
                          "text-sm font-semibold",
                          selectedChangeRequest.new_client_price > selectedChangeRequest.old_client_price ? "text-red-400" : "text-green-400"
                        )}>
                          {selectedChangeRequest.new_client_price > selectedChangeRequest.old_client_price ? "+" : ""}
                          ${(selectedChangeRequest.new_client_price - (selectedChangeRequest.old_client_price || selectedChangeRequest.original_client_price)).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {selectedChangeRequest.change_type === "deadline_change" && (
                  <div className="space-y-2">
                    {(selectedChangeRequest.old_deadline || selectedChangeRequest.original_deadline) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-zinc-400">Current deadline:</span>
                        <span className="text-sm text-red-400 line-through">
                          {formatDate(selectedChangeRequest.old_deadline || selectedChangeRequest.original_deadline)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">New deadline:</span>
                      <span className="text-sm text-green-400 font-semibold">
                        {selectedChangeRequest.new_deadline 
                          ? formatDate(selectedChangeRequest.new_deadline)
                          : "TBD"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Reason */}
              {selectedChangeRequest.reason && (
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">Reason for change:</p>
                  <p className="text-sm text-zinc-300">{selectedChangeRequest.reason}</p>
                </div>
              )}
              
              {/* Comment input */}
              <div>
                <Label className="text-zinc-400 mb-2 block text-sm">
                  Your comment (optional)
                </Label>
                <Textarea
                  value={changeRequestComment}
                  onChange={(e) => setChangeRequestComment(e.target.value)}
                  placeholder="Add a comment to your decision..."
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
                />
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-2 justify-end pt-2 border-t border-zinc-800">
                <Button
                  variant="outline"
                  onClick={() => handleChangeRequestResponse(selectedChangeRequest.id, "reject")}
                  disabled={submittingChangeRequest}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  {submittingChangeRequest ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 mr-2" />
                  )}
                  Reject
                </Button>
                <Button
                  onClick={() => handleChangeRequestResponse(selectedChangeRequest.id, "approve")}
                  disabled={submittingChangeRequest}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submittingChangeRequest ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Studio Request Detail Dialog */}
      <Dialog open={!!selectedStudioRequest} onOpenChange={(open) => { if (!open) { setSelectedStudioRequest(null); setStudioRequestComment(""); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
              Request from Studio
            </DialogTitle>
          </DialogHeader>
          
          {selectedStudioRequest && (
            <div className="space-y-4 mt-2">
              {/* Request info */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge className={cn(
                    "text-xs",
                    selectedStudioRequest.priority === "urgent" && "bg-red-500/20 text-red-400",
                    selectedStudioRequest.priority === "high" && "bg-orange-500/20 text-orange-400",
                    selectedStudioRequest.priority === "normal" && "bg-blue-500/20 text-blue-400",
                    selectedStudioRequest.priority === "low" && "bg-zinc-500/20 text-zinc-400",
                  )}>
                    {selectedStudioRequest.priority} priority
                  </Badge>
                  <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                    {selectedStudioRequest.request_type?.replace(/_/g, " ")}
                  </Badge>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-zinc-50">{selectedStudioRequest.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1">{selectedStudioRequest.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="text-xs text-zinc-500">Project</p>
                    <p className="text-sm text-zinc-50">{selectedStudioRequest.project_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Created</p>
                    <p className="text-sm text-zinc-50">{formatDate(selectedStudioRequest.created_at)}</p>
                  </div>
                  {selectedStudioRequest.price_per_unit > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500">Price per unit</p>
                      <p className="text-sm text-zinc-50 font-mono">${selectedStudioRequest.price_per_unit}</p>
                    </div>
                  )}
                  {selectedStudioRequest.quantity > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500">Quantity</p>
                      <p className="text-sm text-zinc-50">{selectedStudioRequest.quantity}</p>
                    </div>
                  )}
                  {selectedStudioRequest.discount > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500">Discount</p>
                      <p className="text-sm text-orange-400">{selectedStudioRequest.discount}%</p>
                    </div>
                  )}
                  {(selectedStudioRequest.amount > 0 || (selectedStudioRequest.price_per_unit > 0 && selectedStudioRequest.quantity > 0)) && (
                    <div>
                      <p className="text-xs text-zinc-500">Total Amount</p>
                      <p className="text-sm text-green-400 font-medium font-mono">
                        ${(selectedStudioRequest.amount || (selectedStudioRequest.price_per_unit * (selectedStudioRequest.quantity || 1) * (1 - (selectedStudioRequest.discount || 0) / 100))).toFixed(2)}
                      </p>
                    </div>
                  )}
                  {selectedStudioRequest.deadline && (
                    <div>
                      <p className="text-xs text-zinc-500">Deadline</p>
                      <p className="text-sm text-zinc-50">{formatDate(selectedStudioRequest.deadline)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-zinc-500">Requested by</p>
                    <p className="text-sm text-zinc-50">{selectedStudioRequest.created_by_name}</p>
                  </div>
                </div>
              </div>
              
              {selectedStudioRequest.status === "pending" ? (
                <>
                  {/* Comment input */}
                  <div>
                    <Label className="text-zinc-400 mb-2 block text-sm">
                      Your comment (optional)
                    </Label>
                    <Textarea
                      value={studioRequestComment}
                      onChange={(e) => setStudioRequestComment(e.target.value)}
                      placeholder="Add a comment to your decision..."
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
                    />
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex gap-2 justify-end pt-2 border-t border-zinc-800">
                    <Button
                      variant="outline"
                      onClick={() => handleStudioRequestResponse(selectedStudioRequest.id, "reject")}
                      disabled={submittingStudioRequest}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      {submittingStudioRequest ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      )}
                      Reject
                    </Button>
                    <Button
                      onClick={() => handleStudioRequestResponse(selectedStudioRequest.id, "approve")}
                      disabled={submittingStudioRequest}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {submittingStudioRequest ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Approve
                    </Button>
                  </div>
                </>
              ) : (
                /* Already responded */
                <div className={cn(
                  "p-3 rounded-lg",
                  selectedStudioRequest.status === "approved" ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"
                )}>
                  <div className="flex items-center gap-2">
                    {selectedStudioRequest.status === "approved" ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                    )}
                    <span className={cn(
                      "font-medium",
                      selectedStudioRequest.status === "approved" ? "text-green-400" : "text-red-400"
                    )}>
                      {selectedStudioRequest.status === "approved" ? "Approved" : "Rejected"} by {selectedStudioRequest.responded_by_name}
                    </span>
                  </div>
                  {selectedStudioRequest.response_comment && (
                    <p className="text-sm text-zinc-400 mt-2">{selectedStudioRequest.response_comment}</p>
                  )}
                  <p className="text-xs text-zinc-500 mt-1">{formatDate(selectedStudioRequest.responded_at)}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
